// 本檔逐一對應實作計畫的「API 契約」。
// 金額欄位一律是字串(例 "1234.00"),不用 JSON number,避免前端浮點誤差。

/** 金額字串,例 "1234.00"。前端一律以 decimal.js 運算,不用 number。 */
export type Money = string;
/** 日期字串,格式 YYYY-MM-DD。 */
export type IsoDate = string;
/** 時間戳字串,格式 ISO 8601,例 "2026-09-10T03:00:00Z"。 */
export type IsoDateTime = string;

/**
 * 角色。第十二輪從二元變三元(設計規格 §2.1):
 *
 * - `OWNER` 是所有權限的聯集 —— 凡是財務做得到的,老闆都做得到。
 * - `STAFF` 是業務同仁,報價單、品項庫、客戶、範本都動得了。
 * - `FINANCE` 是財務:**報價單唯讀**(看得到金額與單據,但一個字都改不了),
 *   照常請假,不碰業務設定。
 */
export type UserRole = "OWNER" | "STAFF" | "FINANCE";

/** 設定 → 個人資料,來自 `GET /api/users/me/profile`。角色與年度特休只能看。 */
export interface MyProfile {
  email: string;
  name: string;
  phone: string;
  role: UserRole;
  annual_leave_days: string | number;
  /** 入職日 YYYY-MM-DD。老闆設的,這裡只能看;還沒填是 null。 */
  hire_date?: string | null;
  /** 現居地址。本人自己改。 */
  home_address?: string;
}

/**
 * 這個角色動不動得了報價單的內容(含附件)。
 *
 * 判斷收攏在這裡一支,**不讓 `role === "FINANCE"` 這種字串比對散到元件裡** ——
 * 散出去之後,第 15 輪再加一個角色時一定會漏掉其中幾處,而漏掉的那幾處
 * 會是「某個角色意外改得動別人的單」,不是畫面上看得出來的錯。
 *
 * 未登入(`undefined`)一律當成動不了:守衛還沒判定完的那一瞬間,
 * 畫面上寧可少一顆按鈕,也不要先亮出一顆按下去會 401 的。
 */
export function canWriteQuotes(role: UserRole | undefined | null): boolean {
  return role === "OWNER" || role === "STAFF";
}

/**
 * 這個角色刪不刪得掉報價單。
 *
 * 與 `canWriteQuotes` 是**兩個不同的問題**,不可以合併:刪除只有老闆
 * (業主原話,第 11 輪的規矩),而同仁寫得動卻刪不得。後端也是兩支不同的
 * 守門(`require_owner` 與 `require_business`)。
 *
 * 一樣收攏在這裡一支:封存區原本寫的是 `user?.role === "OWNER"`,而那正是
 * 同一頁漏掉「復原也要按角色擋」的原因 —— 散出去的字串比對,加角色的那一輪
 * 不會有任何地方提醒你這裡也要重新回答一次。
 */
export function canDeleteQuotes(role: UserRole | undefined | null): boolean {
  return role === "OWNER";
}

/**
 * 角色動不了報價單時,畫面上要說的那一句。
 *
 * 與後端 `security.require_business` 回的 403 detail **是同一句**:按鈕的提示
 * 與真的送出去會收到的錯誤講的必須是同一件事,否則使用者只會覺得兩邊各說各話
 * (與 `QuoteEditor` 的 `ARCHIVED_IS_READ_ONLY` 同一個道理)。
 *
 * 權限不可以只靠反灰或顏色 —— 這一句是那顆灰掉的按鈕留給人的唯一理由。
 */
export const QUOTE_IS_READ_ONLY_FOR_ROLE = "財務帳號對業務資料唯讀";

/**
 * 稅制只有兩種。業主 2026-09-11 定案:「就很簡單 選擇未稅 總額就是小計+小計的5%
 * 含稅就是 總額=小計 根本沒有免稅這詞」—— `NONE`(免稅)已從系統移除,
 * 後端的列舉與 CHECK 約束也一併拿掉了,這裡不留第三種。
 */
export type TaxMode = "EXCLUSIVE" | "INCLUSIVE";
/**
 * 報價單的四個狀態。「未成交」2026-09-30、「已回簽」「發包製作」2026-10-15 拿掉(業主)。
 *
 * 順序就是案子正常會走的路,兩個「沒成」的結局排在最後 —— 與後端
 * `routers/quotes.py` 的 `STATUS_TEXT` 一字不差。**但系統不做狀態機**
 * (§1.2):任何狀態都轉得到任何狀態,唯一的例外是「有開票申請時退不出
 * 已結案」,而那一條由後端擋。
 */
export type QuoteStatus =
  | "DRAFT"
  | "SENT"
  | "CLOSED"
  | "CANCELLED";
export type SectionMode = "DETAILED" | "LUMP_SUM";
export type LineLayout = "STANDARD" | "MANPOWER";
export type LineKind = "ITEM" | "SUBHEADING" | "NOTE";
export type EmailStatus = "QUEUED" | "SENT" | "FAILED";
export type ApplyTemplateMode = "replace" | "append";
/** 報價分類的發包狀態。純粹是施工管理用的標記,不影響任何金額計算。 */
export type OutsourceStatus = "NOT_ORDERED" | "ORDERED" | "PENDING";

// 狀態的中文字樣**不在這裡**,故意的(設計規格 §1.4:後端是唯一來源)。
// 前端曾經留過一份 `QUOTE_STATUS_LABEL`,而下拉讀 API、徽章讀那一份 ——
// 後端改一個措辭(這一輪就把 CANCELLED 從「已作廢」改成「已取消」),同一個
// 畫面上兩處就會顯示不同的字,而兩套測試都是綠的。要字樣請用
// `api/quoteStatusLabels.ts` 的 `useQuoteStatusLabel()`。

/** 發包狀態一律帶文字,不可只靠顏色。 */
export const OUTSOURCE_STATUS_LABEL: Record<OutsourceStatus, string> = {
  NOT_ORDERED: "未發包",
  ORDERED: "已發包",
  PENDING: "待確認",
};

export const TAX_MODE_LABEL: Record<TaxMode, string> = {
  EXCLUSIVE: "未稅報價(外加營業稅)",
  INCLUSIVE: "含稅報價(總額即小計)",
};

/* ---------- auth ---------- */

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  /** 亮色 / 暗色主題,存在帳號上;null 或沒帶 = 跟著系統設定(業主 2026-09-25)。 */
  theme?: "light" | "dark" | null;
}

/**
 * 可以指派為專案負責同仁的一列,來自 `GET /api/users/pickable`。
 *
 * **只有這三個欄位。** 那一支端點不是老闆專屬(選負責同仁是日常作業),
 * 所以角色、狀態、`can_view_cost` 這些管理資訊一個都不給 ——
 * 完整的帳號清單仍然只有 `GET /api/users` 才有,而那支是老闆專屬。
 */
export interface UserPick {
  id: number;
  name: string;
  email: string;
  phone: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  /** 「記住我」:30 天的滑動 session。不勾 = 原本的 12 小時。 */
  remember: boolean;
}

/* ---------- quotes ---------- */

export interface QuoteLine {
  id: number | null;
  /**
   * 存檔不會變的識別碼。`id` 每存一次檔就換一批(後端整份砍掉重建),
   * `uid` 不會 —— 差異對照與「復原這一列」認得出「同一列」靠的就是它。
   * 後端一律回傳;前端新增的列自己先發一個,送出時原樣帶回去。
   */
  uid?: string;
  sort_order: number;
  kind: LineKind;
  /** 摘要 / 服務項目,允許換行。 */
  name: string;
  /** 規格,允許換行。 */
  spec: string;
  unit: string;
  /** STANDARD 版面使用。 */
  qty: string | null;
  /** 以下四欄為 MANPOWER 版面使用。 */
  service_date: IsoDate | null;
  time_range: string | null;
  headcount: string | null;
  days: string | null;
  unit_price: Money;
  unit_cost: Money | null;
  /** 後端重算後寫入,送出時會被忽略。 */
  amount: Money;
  /** 顯示「(預估)」。 */
  is_estimate: boolean;
  remark: string;
  /** 由品項庫帶入時留下追溯,可為 null。 */
  variant_id: number | null;
}

export interface QuoteSection {
  id: number | null;
  /** 與 QuoteLine.uid 同一回事:存檔不會變,「復原這個分類」靠它指名。 */
  uid?: string;
  sort_order: number;
  /** 顯示用序號,如「一」;可空,空則不顯示。 */
  code: string;
  title: string;
  mode: SectionMode;
  line_layout: LineLayout;
  /** mode = LUMP_SUM 時使用。 */
  lump_sum_amount: Money | null;
  /** 發包狀態。後端一律回傳;舊資料或半成品沒有這一欄時視為未發包。 */
  outsource_status?: OutsourceStatus;
  note: string;
  /** 後端重算後寫入,送出時會被忽略。 */
  subtotal: Money;
  lines: QuoteLine[];
}

/**
 * 甲方(我方)。**五個欄位全部是推導值**:公司名稱與聯絡地址取自公司設定,
 * 承辦人姓名／電話／EMAIL 取自 `owner_user_id` 指向的那位同仁。
 * `PUT` 送上去一律被後端忽略,要改承辦人電話請去使用者管理改。
 */
export interface PartyA {
  company_name: string;
  address: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
}

/**
 * 乙方(客戶)。同樣是推導值:統編與公司地址跟著 `client_company_id` 指的
 * 客戶檔走,聯絡人／聯絡電話／EMAIL 則是這張單自己的 `contact_*` 欄位。
 *
 * 電話有三欄,**不是重複**:`contact_mobile` 與 `contact_landline` 是這張單上
 * 實際存著的兩支;`contact_phone` 是併好的那一行
 * (`0988-555-222 | 04-2691-5555#555`),PDF 與 XLSX 直接印它。
 */
export interface PartyB {
  company_name: string;
  tax_id: string;
  contact_name: string;
  /** 手機。後端一律回傳;舊的測試資料沒有這一欄時視為空白。 */
  contact_mobile?: string;
  /** 市話。同上。 */
  contact_landline?: string;
  contact_phone: string;
  contact_email: string;
  address: string;
}

export interface QuoteDetail {
  id: number;
  /** 格式 NB-YYYY-NNNN。 */
  quote_no: string;
  /** 專案名稱。 */
  title: string;
  /** 專案時間,刻意用自由文字。 */
  event_period: string;
  /**
   * 專案檔期的真日期,**只給日曆用**,與上面那行自由文字並存,兩者互不覆寫。
   *
   * 業主原檔寫的是「2026年12月12日(六)-12月13日(日)」,也可能是
   * 「12/12 下午 – 12/13 全日」—— 那串字是印給客戶看的,系統解析不了,
   * 硬猜出來的日期會讓排假的人看到錯的檔期。所以另存一組真日期,
   * 兩欄都填了才會出現在日曆上;沒填就是不出現,那比猜錯好。
   *
   * **宣告成必填(值可以是 null),不是選填。** 寫成 `?` 的話,草稿與存檔
   * payload 漏掉這兩欄 typecheck 一聲都不吭,而後端收到的就是 None ——
   * 存一次檔就把檔期清掉,那張單從日曆上消失,畫面上還看不出來。
   */
  event_start_date: IsoDate | null;
  event_end_date: IsoDate | null;
  /** 專案地點。 */
  venue: string;
  /**
   * 專案負責同仁,也就是甲方的專案承辦人。留空代表「先開單、之後再派人」。
   * 後端一律回傳;半成品或舊的測試資料沒有這一欄時視為未指派。
   */
  owner_user_id?: number | null;
  client_company_id: number | null;
  client_name_override: string | null;
  contact_name: string;
  /**
   * 乙方窗口的手機。與 `contact_phone`(市話)是兩欄,顯示時才併成一行 ——
   * 業主:「如果電話有兩個 手機或者市內 那我們顯示就要顯示兩個」。
   * 後端一律回傳;舊的測試資料沒有這一欄時視為空白。
   */
  contact_mobile?: string;
  contact_phone: string;
  contact_email: string;
  quote_date: IsoDate;
  valid_until: IsoDate | null;
  tax_mode: TaxMode;
  /** 稅率字串,例 "0.0500"。資料庫存的是小數,畫面上才換算成百分比。 */
  tax_rate: string;
  /**
   * 稅率的百分比寫法,唯讀,例 "5"。後端算好一份,前端顯示時另外用
   * `lib/money.ts` 的 `rateToPercent` 算一份 —— 兩邊各有測試釘住 5 ↔ 0.0500。
   */
  tax_rate_percent?: string;
  status: QuoteStatus;
  /** 甲方／乙方兩塊唯讀的推導區塊,`PUT` 送上去會被忽略。 */
  party_a?: PartyA;
  party_b?: PartyB;
  /**
   * 一口價(業主 2026-10-01),整數字串,沒設是 null。有設就取代小計去算稅與總計;
   * `subtotal` 仍是明細加總的原價(畫面與 PDF 上畫刪除線的那個)。
   */
  special_price?: string | null;
  /** 以下三欄由後端重算並覆寫。 */
  subtotal: Money;
  tax_amount: Money;
  total: Money;
  notes: string;
  terms: string;
  /** 樂觀鎖,PUT 必須帶,不符回 409。 */
  version: number;
  sections: QuoteSection[];
  /**
   * 付款條件(第 13 輪,設計規格 §2)。**唯讀**,與後端同一條紀律:改它的唯一
   * 入口是 `PUT /api/quotes/{id}/payment-terms`,`PUT /api/quotes/{id}` 送什麼
   * 上來一律忽略,所以它不在 `QuoteUpdate` 裡。
   *
   * 標成選填是因為舊的測試資料與半成品沒有這一欄 —— 前端不該假設一定收得到。
   */
  payment_terms?: QuotePaymentTerm[];
  /**
   * 已結案的單走到哪一步了(業主 2026-09-16):
   * `REQUESTED` 申票中、`INVOICED` 已請票、`NONE` 兩者皆非。
   *
   * **推導值,後端不存這一欄** —— 存的話就要有人讓它跟開票申請與發票保持同步。
   */
  billing_state?: "NONE" | "REQUESTED" | "INVOICED";
  /** 中文字樣由後端給。`NONE` 是空字串 —— 那一格什麼都不顯示。 */
  billing_label?: string;
}

/**
 * 與 QuoteDetail 同形;amount / subtotal / tax_amount / total 會被後端忽略。
 * `party_a` / `party_b` / `tax_rate_percent` 是推導值,連送都不送 ——
 * 送了也被忽略,留在 payload 裡只會讓人以為改得動。
 */
export type QuoteUpdate = Omit<
  QuoteDetail,
  "id" | "quote_no" | "status" | "party_a" | "party_b" | "tax_rate_percent" | "payment_terms"
>;

export type QuoteCreate = Partial<QuoteUpdate> & { title: string };

/* ---------- 狀態與付款條件(第 13 輪,設計規格 §1、§2) ---------- */

/**
 * 一個狀態的識別字與它的中文字樣,來自 `GET /api/quote-statuses`。
 *
 * **後端是字樣的唯一來源**(設計規格 §1.4)。狀態下拉一律讀這一份,不從前端
 * 自己的對照表拼 —— 兩份遲早會漂開,而漂開的那一天畫面上會出現英文代碼。取它的入口是 `api/client.ts` 的 `loadQuoteStatuses()`。
 */
export interface StatusOption {
  value: QuoteStatus;
  label: string;
}

/**
 * 狀態歷程上的一列,來自 `GET /api/quotes/{id}/status-events`(**最新在前**)。
 *
 * **系統不擋任何一次轉換,但每一次都留在這裡**(設計規格 §1.2):畫面要答得出
 * 「這張單是什麼時候被誰改成已結案的」,所以兩端、人、時間四樣都在同一列上。
 *
 * `from_label` / `to_label` 由後端一起攤平送來,前端不必先載過
 * `GET /api/quote-statuses` 才畫得出歷程 —— 歷程是點進單子就要看得到的東西,
 * 不該依賴另一支端點有沒有回來過。
 *
 * `actor_name` 是空字串時,代表那位同仁的帳號已經被刪掉
 * (`ON DELETE SET NULL`):那次轉換還在,只是從此說不出是誰改的。
 */
export interface QuoteStatusEvent {
  id: number;
  from_status: QuoteStatus;
  to_status: QuoteStatus;
  from_label: string;
  to_label: string;
  actor_user_id: number | null;
  actor_name: string;
  created_at: IsoDateTime;
}

/**
 * 一期付款條件,來自 `QuoteDetail.payment_terms` 或
 * `GET|PUT /api/quotes/{id}/payment-terms`(設計規格 §2.1)。
 *
 * `percent` 與 `amount` **二擇一**(§2.2):談生意講的是「三七分」,所以比例是
 * 主要的輸入方式;但「訂金先收 50,000」這種講定的數字也要填得進去。同一期兩欄
 * 都填,存檔會得到一個**指得出是第幾期**的 422。兩欄都空著是合法的 ——
 * 寫到一半本來就會有還沒填完的列。
 *
 * `amount` 在比例期上一律是 `null`(後端的 validator 清掉了):資料表那一欄存的
 * 是後端算完釘在那裡的金額,不是使用者填的數字。**實際金額一律看
 * `resolved_amount`。**
 *
 * `invoiced_amount > 0` 的期別**永不重算、不可刪**(§2.5)。發票要到第 15 輪
 * 才存在,這一欄現在恆為 `"0.00"`,但畫面現在就要讀它 ——
 * 防線要先於它要防的東西存在。
 */
export interface QuotePaymentTerm {
  id: number;
  /** 第幾期,從 1 開始。**尾差落在 `seq` 最大的那一期**,不是畫面上排最後的那一列。 */
  seq: number;
  name: string;
  /** 比例,例 `"33.3300"`。與 `amount` 二擇一。 */
  percent: string | null;
  amount: Money | null;
  due_date: IsoDate | null;
  /** 唯讀:後端算出來的本期金額。畫面與 PDF 印的都是它。 */
  resolved_amount: Money;
  /** 唯讀:已開票金額。這一輪恆為 `"0.00"`。 */
  invoiced_amount: Money;
}

/**
 * 送去 `PUT /api/quotes/{id}/payment-terms` 的一期。
 *
 * **整批取代:送什麼就是什麼,沒送到的那一期視為刪除**(與章節的
 * `replace_sections()` 同一種做法)—— 合併語意會讓「刪掉一期」變得無法表達。
 * 三個唯讀欄位一個都不送:送了也被忽略,留在 payload 裡只會讓人以為改得動。
 */
export interface PaymentTermInput {
  seq: number;
  name: string;
  percent: string | null;
  amount: Money | null;
  due_date: IsoDate | null;
}

/**
 * 清單用的摘要。
 *
 * **這裡要蓋滿後端 `app/columns.py` 的每一個可選欄位** —— 列表要顯示哪幾欄由老闆
 * 在設定頁決定,勾得到卻給不出值,那一欄就是一片空白。
 *
 * 關聯資料在後端就攤平成姓名字串(`client_name` / `owner_name` / `created_by_name`),
 * 不丟外鍵給前端再查一次:列表一頁二十列,每一列都去要一次使用者資料會慢得看得出來。
 *
 * 第六輪就有的那六個維持必填;這一輪補上的幾個標成選填,因為它們是後端後來才加的,
 * 前端不該假設一定收得到(例如尚未更新的後端、或快取住的舊回應)。
 */
export interface QuoteSummary {
  id: number;
  quote_no: string;
  title: string;
  client_name: string;
  quote_date: IsoDate;
  status: QuoteStatus;
  total: Money;
  updated_at: IsoDateTime;
  /** 自由文字的專案檔期,例如「2026年12月12日(六)-12月13日(日)」。 */
  event_period?: string;
  /** 只用日期選擇器、沒打那段文字的單子,靠這兩個日期也畫得出檔期。 */
  event_start_date?: IsoDate | null;
  event_end_date?: IsoDate | null;
  venue?: string;
  owner_name?: string;
  subtotal?: Money;
  tax_amount?: Money;
  version?: number;
  created_by_name?: string;
  /**
   * 已結案的單走到哪一步了(業主 2026-09-16):
   * `REQUESTED` 申票中、`INVOICED` 已請票、`NONE` 兩者皆非。
   *
   * **推導值,後端不存這一欄** —— 存的話就要有人讓它跟開票申請與發票保持同步。
   */
  billing_state?: "NONE" | "REQUESTED" | "INVOICED";
  /** 中文字樣由後端給。`NONE` 是空字串 —— 那一格什麼都不顯示。 */
  billing_label?: string;
  /**
   * 收款狀態(業主 2026-09-30:「老闆/財務 在報價單列表上 要出現 已收款/未收款的TAG」)。
   * **只有老闆與財務拿得到值**;非成交單與其他人一律 null。
   */
  collection?: Collection | null;
}

/** 收款狀態:未收款 / 部分收款 / 已收款。 */
export type Collection = "UNPAID" | "PARTIAL" | "PAID";

/** `GET /api/quotes/{id}/finance-summary`:報價單上方那一條(財務整理 1)。 */
export interface QuoteMoney {
  deal: boolean;
  deal_amount: Money;
  invoice_count: number;
  invoiced_amount: Money;
  received: Money;
  outstanding: Money;
  collection: Collection | null;
  oldest_overdue_days: number | null;
  expense_count: number;
  expense_amount: Money;
  profit: Money;
  margin_percent: Money | null;
  /** 流程條:草稿 → 已寄出 → 已結案 → 申請開票 → 已開票,每格的日期(後端算好,2026-10-15)。 */
  flow?: QuoteFlow;
}

export interface QuoteFlowStep {
  key: string;
  label: string;
  done: boolean;
  /** YYYY-MM-DD;還沒走到、或舊單查不到時是 null。 */
  date: IsoDate | null;
  /** 只有「已開票」:發票上的日期跟開進系統的那天不一樣時才有。 */
  invoice_date?: IsoDate | null;
}

export interface QuoteFlow {
  stopped: boolean;
  current: string | null;
  steps: QuoteFlowStep[];
}

/** 一筆收款,記在報價單(某一期)上(財務整理 B)。 */
export interface QuoteReceipt {
  id: number;
  quote_id: number;
  term_id: number | null;
  paid_on: IsoDate;
  amount: Money;
  note: string;
  recorder_name: string;
  can_delete: boolean;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export interface QuoteListQuery {
  q?: string;
  status?: QuoteStatus;
  page?: number;
  size?: number;
}

/* ---------- 版本紀錄、差異與復原 ---------- */

/**
 * 差異的四種變化。`UNCHANGED` 永遠不會出現在 API 回應裡 ——
 * 沒改的分類與明細後端一律不回傳,那正是這個值的意思。
 * 列在型別裡是為了讓前端知道沒有第五種可能。
 */
export type ChangeKind = "ADDED" | "REMOVED" | "CHANGED" | "UNCHANGED";

/** 變更標記一律「圖示 + 文字」,不可只靠顏色。 */
export const CHANGE_KIND_LABEL: Record<ChangeKind, string> = {
  ADDED: "新增",
  REMOVED: "刪除",
  CHANGED: "修改",
  UNCHANGED: "未變更",
};

/** 復原的三種粒度:整張單 / 某一個分類 / 某一列明細。 */
export type RestoreScope = "QUOTE" | "SECTION" | "LINE";

export interface RevisionSummary {
  version: number;
  created_at: IsoDateTime;
  created_by_name: string;
  subtotal: Money;
  tax_amount: Money;
  total: Money;
  /** 與**前一個版本**的總計差。後端算好,前端不自己減。 */
  total_delta: Money;
  section_count: number;
  line_count: number;
  /** 有值代表這一版由復原產生,值是被復原的來源版本號。 */
  is_restore_of: number | null;
}

/**
 * 一個改過的欄位。`label` 是中文欄位名,由後端 `diffing.py` 維護唯一的一份 ——
 * 前端直接用,不另建對照表;兩邊各存一份,遲早有一邊改了另一邊沒改。
 */
export interface DiffField {
  name: string;
  label: string;
  old: string;
  new: string;
}

/** 一個金額欄的前、後與增減。三個都是字串,不是 JSON number。 */
export interface DiffTotal {
  old: Money;
  new: Money;
  delta: Money;
}

/** 快照裡的一列明細,原樣的一包。展開那一列時要看得到前後完整對照。 */
export type DiffSnapshotRow = Record<string, unknown>;

export interface DiffLine {
  uid: string | null;
  /** 該列在**新版**的第幾列(1 起);REMOVED 的列用它在**舊版**的位置。 */
  position: number;
  name: string;
  change: ChangeKind;
  amount_delta: Money;
  fields: DiffField[];
  /** 新增的列沒有 old,刪掉的列沒有 new。 */
  old: DiffSnapshotRow | null;
  new: DiffSnapshotRow | null;
}

export interface DiffSection {
  uid: string | null;
  code: string;
  title: string;
  change: ChangeKind;
  subtotal_delta: Money;
  /** 明細順序被搬動過。金額沒變,但報價單印出來就是那個順序,算改動。 */
  lines_reordered: boolean;
  fields: DiffField[];
  /** 只有真的動過的列。 */
  lines: DiffLine[];
}

/** 兩個版本之間的差異。**只有變化**,沒改的分類與明細一律不在裡面。 */
export interface QuoteDiff {
  from_version: number | null;
  to_version: number | null;
  /** 鍵是 subtotal / tax_amount / total。 */
  totals: Record<string, DiffTotal>;
  header_fields: DiffField[];
  sections: DiffSection[];
  sections_reordered: boolean;
}

/** 復原請求。`scope` 是 QUOTE 時 `uid` 留 null。 */
export interface RestoreRequest {
  from_version: number;
  scope: RestoreScope;
  uid: string | null;
}

/* ---------- templates ---------- */

export interface TemplateSummary {
  id: number;
  name: string;
  description: string;
  is_system: boolean;
  section_count: number;
  line_count: number;
}

export interface TemplateLine {
  id: number;
  sort_order: number;
  kind: LineKind;
  name: string;
  spec: string;
  unit: string;
  qty: string | null;
  headcount: string | null;
  days: string | null;
  unit_price: Money | null;
  remark: string;
}

export interface TemplateSection {
  id: number;
  sort_order: number;
  code: string;
  title: string;
  mode: SectionMode;
  line_layout: LineLayout;
  note: string;
  lines: TemplateLine[];
}

export interface TemplateDetail extends TemplateSummary {
  sections: TemplateSection[];
}

export interface TemplateCreate {
  name: string;
  description: string;
  from_quote_id: number;
}

export interface ApplyTemplateRequest {
  template_id: number;
  include_prices: boolean;
  mode: ApplyTemplateMode;
}

/* ---------- catalog ---------- */

export interface CatalogHit {
  variant_id: number;
  item_name: string;
  spec: string;
  unit: string;
  price: Money;
  category: string;
}

export interface Category {
  id: number;
  name: string;
  sort_order: number;
}

export interface ItemVariant {
  id: number;
  spec: string;
  unit: string;
  price: Money;
}

export interface Item {
  id: number;
  category_id: number;
  name: string;
  variants: ItemVariant[];
}

/* ---------- companies / settings ---------- */

export interface Company {
  id: number;
  name: string;
  /** 業主口中的「行號」就是統一編號,沿用這一欄,不另開新欄位。 */
  tax_id: string;
  address: string;
  contact_name: string;
  /** 手機電話。與市內電話分開存,業主找人時打的是這一支。 */
  contact_mobile: string;
  contact_phone: string;
  contact_email: string;
  /**
   * 停用的客戶從清單與搜尋裡消失、開新單選不到,但**資料還在**,
   * 舊報價單上的客戶名稱也還在。業主 2026-09-18 決定不要真的刪:
   * 客戶一旦消失,歷史單據會跟著壞。
   */
  is_active: boolean;
  /** 應收(2026-09-30,財務整理 4):成交單的未收加總。只有老闆、而且帶 `receivables=true` 時才有值。 */
  receivable?: Money | null;
  /** 最久欠幾天。收齊了或沒有應收是 null。 */
  oldest_overdue_days?: number | null;
}

export type CompanyCreate = Omit<Company, "id" | "is_active" | "receivable" | "oldest_overdue_days">;

/**
 * 刪掉這家客戶會牽連到的一張報價單。
 *
 * 業主 2026-09-20:「刪除時跳出來會牽連到哪一些報價單 確定是否刪除
 * 哪一些報價單要可以點進去」。所以 `id` 是拿來組連結的,其餘幾欄是拿來
 * **認出這是哪一張**的。
 */
export interface CompanyImpactQuote {
  id: number;
  quote_no: string;
  title: string;
  status: QuoteStatus;
  quote_date: string | null;
  total: string;
  /** 已封存。**看不到的那幾張才是會出事的**,畫面上要標出來。 */
  archived: boolean;
}

/** 刪掉這家客戶會發生什麼。 */
export interface CompanyImpact {
  id: number;
  name: string;
  is_active: boolean;
  /** 全部,含已封存 —— 這是要一張一張看的清單,不是分頁列表。 */
  quotes: CompanyImpactQuote[];
  /** 這兩樣會跟著客戶一起消失。 */
  contacts: number;
  portal_users: number;
}

/**
 * 客戶公司的一個窗口。**一間公司可以有很多個。**
 *
 * 業主 2026-09-18:「一間公司 可能會有很多窗口 負責的案件不同」。
 * `Company` 上那一組 `contact_*` 只存得下一個人,年會跟攤位常常不同人管。
 */
/**
 * 回收桶裡的一列(業主 2026-09-20:「7天內可以恢復任何東西」)。
 *
 * 七種東西混在一張清單上,所以每一列都要答得出四件事:
 * **是哪一種、叫什麼、誰丟的、還剩幾天。**
 */
export interface TrashItem {
  /** 網址上那一段:`POST /api/trash/{kind}/{id}/restore`。 */
  kind: string;
  /** 畫面上那一欄寫的字:「會議紀錄」「雜支」…… */
  kind_label: string;
  id: number;
  name: string;
  deleted_by_name: string;
  deleted_at: string | null;
  /** 還剩幾天。**後端算的** —— 前端各算一份的話,時區差一小時就會差一天。 */
  days_left: number;
}

export interface CompanyContact {
  id: number;
  company_id: number;
  name: string;
  title: string;
  /** 這個人負責哪一塊(「2026 年會」「攤位」)。業主要的就是這一欄。 */
  duty: string;
  mobile: string;
  phone: string;
  email: string;
  note: string;
}

export type CompanyContactDraft = Omit<CompanyContact, "id" | "company_id">;

export interface CompanySettings {
  company_name: string;
  /** 業主統編。正確值見設定頁,不要在註解裡抄一份會過期的。 */
  tax_id: string;
  address: string;
  /** 業主電話。同上,以設定頁為準。 */
  phone: string;
  fax: string;
  email: string;
  bank_info: string;
  default_terms: string;
  default_tax_rate: string;
}

/* ---------- emails ---------- */

export interface EmailAttachment {
  filename: string;
  size: number;
}

export interface SendRequest {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  attach_pdf: boolean;
  attach_xlsx: boolean;
}

export interface EmailLog {
  id: number;
  quote_id: number;
  to_addrs: string[];
  cc_addrs: string[];
  subject: string;
  body: string;
  attachments: EmailAttachment[];
  status: EmailStatus;
  error: string | null;
  sent_at: IsoDateTime | null;
}

/** 「系統寄出」列表的一列(`GET /api/mailbox/system-sent`):系統替本人寄的信。 */
export interface SystemSentItem {
  id: number;
  to_addrs: string[];
  cc_addrs: string[];
  subject: string;
  /** 寄出時間;還沒寄出(寄失敗)用建立時間。 */
  date: string | null;
  status: EmailStatus;
  error: string | null;
  has_attachments: boolean;
  /** 有沒有存下寄出去的原信(舊紀錄沒有)。 */
  has_raw: boolean;
  quote_id: number | null;
}

/** 舊紀錄的附件:只有檔名與大小(沒有原信就沒有東西可下載)。 */
export interface SystemSentLegacyAttachment {
  filename: string;
  size: number;
}

/**
 * 讀一筆(`GET /api/mailbox/system-sent/{id}`)。有原信時與 `MailDetail` 同一套(`text` / `html` 已清洗、
 * `attachments` 有 `part`);`legacy` 時是當時記下的收件者、主旨、內文與附件檔名。
 */
export interface SystemSentDetail extends SystemSentItem {
  from_addr: string;
  on_behalf_of: string;
  quote_no: string | null;
  sent_at: string | null;
  legacy: boolean;
  /** 有記雜湊、檔案卻不在。 */
  raw_missing: boolean;
  text: string;
  html: string;
  attachments: Array<MailAttachment | SystemSentLegacyAttachment>;
  remote_images_blocked: number;
  inline_images_dropped: number;
}

/* ---------- 全域搜尋 ---------- */

export interface GlobalSearchResult {
  quotes: QuoteSummary[];
  catalog: CatalogHit[];
  templates: TemplateSummary[];
}

export interface HealthResponse {
  ok: boolean;
}

/* ---------- 日曆 ---------- */

/**
 * 日曆上一格的來源。目前只有報價單的專案檔期,休假另外由 `LeaveRequest` 直接畫。
 * 前端不靠「有沒有 quote_no」這種形狀差異去猜這一條是什麼 ——
 * 那種判斷會在多一種來源時安靜地判錯。
 */
export type CalendarEventKind = "QUOTE";

/** `GET /api/calendar/events?from=&to=` 的一列:一段專案檔期。 */
export interface CalendarEvent {
  kind: CalendarEventKind;
  quote_id: number;
  quote_no: string;
  title: string;
  start_date: IsoDate;
  end_date: IsoDate;
  status: QuoteStatus;
  /**
   * 以下三欄是給日曆上的專案摘要卡用的(業主 2026-09-13:「摘要卡片 + 一鍵開單」)。
   * 後端一律給得出值,還沒指定客戶或負責同仁的單子是空字串,不是 null ——
   * 先開單後派人是常態,卡片照樣要畫得出來。
   */
  client_name: string;
  owner_name: string;
  /** 金額字串。JSON number 到了瀏覽器是浮點數,一經過就對不回來了。
   *
   *  **卡片上不畫它**(業主 2026-09-23:「點開專案後 不用顯示金額」)。
   *  欄位留著是因為拿掉會連帶刪掉後端兩條在守「金額一律字串」的測試,
   *  而那條規矩與這一輪無關。 */
  total: Money;
  /** 發包狀況,數的是**報價分類**(業主是以分類為單位在發包)。
   *  三個狀態都給:「待確認」併進未發包的話,已經跟廠商講好、只差沒回覆的
   *  那幾類就看不出來了。 */
  outsource_ordered: number;
  outsource_not_ordered: number;
  outsource_pending: number;
}

/** 老闆的全員出勤總表上的一列(業主 2026-09-23:「一次看到所有人」)。 */
export interface AttendanceSummaryRow {
  user_id: number;
  name: string;
  /** 這個帳號還在不在職。**停用的人只要那個月有出勤就照樣列** ——
   *  九月做到一半離職的人,九月的工時還是要領薪水的。 */
  active: boolean;
  worked_days: number;
  /** 一位小數的字串,與月表同一個寫法。 */
  total_hours: string;
  /** 請了幾天假,半天是 0.5(字串,例如 "2.5")。 */
  leave_days: string | number;
}

/** `GET /api/attendance/summary?year=&month=`。最外層那三個數是全公司小計。 */
export interface AttendanceSummary {
  year: number;
  month: number;
  rows: AttendanceSummaryRow[];
  worked_days: number;
  total_hours: string;
  /** 請了幾天假,半天是 0.5(字串,例如 "2.5")。 */
  leave_days: string | number;
}

/* ---------- 請假 ---------- */

/**
 * 休假類別。業主 2026-09-12 列的十三種,順序照原文,與後端 `LeaveType` 一致。
 * **只有 ANNUAL(特休)會扣額度**,其餘十二種純粹是紀錄。
 */
export type LeaveType =
  | "ANNUAL"
  | "PERSONAL"
  | "SICK"
  | "MENSTRUAL"
  | "MARRIAGE"
  | "BEREAVEMENT"
  | "OFFICIAL"
  | "OCCUPATIONAL"
  | "MATERNITY"
  | "PRENATAL"
  | "PATERNITY"
  | "FAMILY_CARE"
  | "PARENTAL";

/** 下拉選單的順序。照業主原文,不照字母也不照使用頻率。 */
export const LEAVE_TYPES: LeaveType[] = [
  "ANNUAL",
  "PERSONAL",
  "SICK",
  "MENSTRUAL",
  "MARRIAGE",
  "BEREAVEMENT",
  "OFFICIAL",
  "OCCUPATIONAL",
  "MATERNITY",
  "PRENATAL",
  "PATERNITY",
  "FAMILY_CARE",
  "PARENTAL",
];

/** 正式名稱,用在下拉選單與清單。 */
export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  ANNUAL: "特別休假(特休)",
  PERSONAL: "事假",
  SICK: "普通傷病假(病假)",
  MENSTRUAL: "生理假",
  MARRIAGE: "婚假",
  BEREAVEMENT: "喪假",
  OFFICIAL: "公假(補假)",
  OCCUPATIONAL: "公傷病假",
  MATERNITY: "產假",
  PRENATAL: "產檢假",
  PATERNITY: "陪產檢及陪產假",
  FAMILY_CARE: "家庭照顧假",
  PARENTAL: "育嬰留職停薪",
};

/**
 * 日曆格子裡的短名。月曆一格只有幾個字的寬度,
 * 「特別休假(特休)」擠進去就把姓名撞掉了 —— 而姓名是那一條最該看見的東西。
 */
export const LEAVE_TYPE_SHORT_LABEL: Record<LeaveType, string> = {
  ANNUAL: "特休",
  PERSONAL: "事假",
  SICK: "病假",
  MENSTRUAL: "生理假",
  MARRIAGE: "婚假",
  BEREAVEMENT: "喪假",
  OFFICIAL: "公假",
  OCCUPATIONAL: "公傷病假",
  MATERNITY: "產假",
  PRENATAL: "產檢假",
  PATERNITY: "陪產假",
  FAMILY_CARE: "家庭照顧假",
  PARENTAL: "育嬰停薪",
};

/** 請假的時段:整天 / 上午 / 下午。天數以 0.5 為單位就是靠這一欄。 */
export type LeavePeriod = "FULL" | "AM" | "PM";

export const LEAVE_PERIOD_LABEL: Record<LeavePeriod, string> = {
  FULL: "全天",
  AM: "上午",
  PM: "下午",
};

export type LeaveStatus = "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";

/** 狀態一律「文字」,不可只靠顏色分辨。 */
export const LEAVE_STATUS_LABEL: Record<LeaveStatus, string> = {
  PENDING: "待核准",
  APPROVED: "已核准",
  REJECTED: "已駁回",
  CANCELLED: "已取消",
};

/**
 * 一張假單。`days` 是字串(例 "1.5"),與金額同一個規矩:
 * 它會被拿去扣特休額度,一律以 decimal.js 運算,不用 number。
 */
export interface LeaveRequest {
  id: number;
  user_id: number;
  /** 申請人姓名。日曆要寫出「哪一天誰不在」,只有 id 畫不出來。 */
  user_name: string;
  leave_type: LeaveType;
  start_date: IsoDate;
  start_period: LeavePeriod;
  end_date: IsoDate;
  end_period: LeavePeriod;
  /** 按小時的假(1~8,業主 2026-11-03);整天/半天的假是 null。 */
  hours?: number | null;
  /** 按小時的假從幾點開始(整點 0~23),結束 = start_hour + hours;舊單與整天/半天是 null。 */
  start_hour?: number | null;
  /** 天數字串,例 "1.5"、按小時是 "0.25"。**由後端算**,前端送什麼都不算數。 */
  days: string;
  reason: string;
  status: LeaveStatus;
  decided_by: number | null;
  decider_name: string;
  decided_at: IsoDateTime | null;
  decision_note: string;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

/**
 * 申請一張假單送出去的東西。
 *
 * **沒有 user_id,也沒有 days。** 申請人一律是登入者,天數一律由後端算 ——
 * 送上去也會被忽略,留在型別裡只會讓人以為改得動。
 */
export interface LeaveCreate {
  leave_type: LeaveType;
  start_date: IsoDate;
  start_period: LeavePeriod;
  end_date: IsoDate;
  end_period: LeavePeriod;
  /** 按小時才送(同一天、1~8 小時,天數 = 時數 / 8);整天/半天不送。 */
  hours?: number | null;
  /** 按小時才送:從幾點開始(整點)。 */
  start_hour?: number | null;
  reason: string;
}

/** 老闆的裁決。`approve` 為真是准,為假是駁回;`note` 是駁回原因。 */
export interface LeaveDecision {
  approve: boolean;
  note: string;
}

/** 一個人的年度特休額度。三個數字都是字串,與 `LeaveRequest.days` 同一個規矩。 */
export interface LeaveBalanceRow {
  user_id: number;
  name: string;
  entitled: string;
  used: string;
  /** 額度減掉已核准的特休。**算得出負數就是負數** —— 靜靜地變成 0 會讓人以為還有得請。 */
  remaining: string;
}

/* ---------- 站內通知 ---------- */

/**
 * 通知的種類(業主 2026-09-13 確認的事件清單,與後端 `NotificationKind` 一致)。
 *
 * `kind` 決定畫面上那一列的圖示與措辭,所以同一件事的不同結果共用同一個 kind
 * (核准與駁回都是 `LEAVE_DECIDED`),不同的事一定要分開。
 */
export type NotificationKind =
  | "LEAVE_SUBMITTED"
  | "LEAVE_DECIDED"
  | "LEAVE_REVOKED"
  | "QUOTE_ASSIGNED"
  | "QUOTE_STATUS"
  | "QUOTE_EDITED"
  | "QUOTE_ARCHIVED"
  | "QUOTE_UNARCHIVED"
  | "QUOTE_DELETED"
  | "INVOICE_REQUESTED"
  | "INVOICE_REQUEST_CANCELLED"
  | "INVOICE_ISSUED"
  | "INVOICE_VOIDED"
  | "INVOICE_OVERDUE"
  | "QUOTE_SIGNED_BACK"
  | "TODO_ASSIGNED"
  | "TODO_DONE"
  | "ACCOUNT_CREATED"
  | "ACCOUNT_DISABLED"
  | "ACCOUNT_ROLE"
  | "ACCOUNT_PASSWORD_RESET"
  | "MAIL_SEND_FAILED"
  | "MAIL_FOLLOWUP_DUE"
  | "BACKUP_FAILED"
  | "WORK_ORDER_REVIEW_REQUESTED"
  | "WORK_ORDER_APPROVED"
  | "WORK_ORDER_RETURNED";

/**
 * 每一則通知旁邊那一小行分類。
 *
 * 圖示本身不承載資訊(一律 aria-hidden),看不到圖示的人要靠這一行知道
 * 這是哪一類的事 —— 所以它是文字,不是只有顏色或形狀的差別。
 *
 * **這一份與後端 `NotificationKind` 一個值都不可以少。** TypeScript 攔不住漏值:
 * `Record<NotificationKind, string>` 用 `NotificationKind` 索引永遠合法,漏掉的
 * 那個值只在執行期變成 `undefined`,而畫面上那一列的分類就是一個空的 `<span>` ——
 * 只剩「未讀」徽章與時間,而圖示是 aria-hidden 的,看不到圖示的人什麼都不知道
 * (全域規矩:狀態不可只靠顏色,一律帶文字)。第 11 輪的三個封存 kind 與第 13 輪
 * 的三個開票 kind 就是這樣各自漏了一輪。
 *
 * 現在有一條測試拿後端 `app/models.py` 的清單守著它
 * (`tests/notifications.test.tsx` 的「通知的分類文字」)。
 */
export const NOTIFICATION_KIND_LABEL: Record<NotificationKind, string> = {
  LEAVE_SUBMITTED: "待審核假單",
  LEAVE_DECIDED: "請假裁決",
  LEAVE_REVOKED: "假單撤銷",
  QUOTE_ASSIGNED: "指派負責",
  QUOTE_STATUS: "報價單狀態",
  QUOTE_EDITED: "報價單異動",
  QUOTE_ARCHIVED: "報價單封存",
  QUOTE_UNARCHIVED: "報價單復原",
  QUOTE_DELETED: "報價單刪除",
  INVOICE_REQUESTED: "開票申請",
  INVOICE_REQUEST_CANCELLED: "開票申請撤銷",
  INVOICE_ISSUED: "發票開立",
  INVOICE_VOIDED: "發票作廢",
  INVOICE_OVERDUE: "逾期未收",
  QUOTE_SIGNED_BACK: "回簽上傳",
  TODO_ASSIGNED: "交辦事項",
  TODO_DONE: "代辦完成",
  ACCOUNT_CREATED: "帳號建立",
  ACCOUNT_DISABLED: "帳號停用",
  ACCOUNT_ROLE: "職稱異動",
  ACCOUNT_PASSWORD_RESET: "密碼重設",
  MAIL_SEND_FAILED: "寄信失敗",
  MAIL_FOLLOWUP_DUE: "待回覆提醒",
  BACKUP_FAILED: "系統備份",
  WORK_ORDER_REVIEW_REQUESTED: "工單待覆核",
  WORK_ORDER_APPROVED: "工單覆核通過",
  WORK_ORDER_RETURNED: "工單退回",
};

/**
 * 一則通知。
 *
 * 名字不叫 `Notification`:那是瀏覽器內建的桌面通知類別,同名會讓
 * 「這個 Notification 是哪一個」變成每次都要回頭確認的事。
 *
 * - `link` 後端保證不是空字串。點了通知卻不知道要去哪,那則通知等於沒有用。
 * - `read_at` 是 `null` 就是未讀。**未讀與已讀不可以只靠底色分**,
 *   畫面上要另外帶文字(見 `NotificationPanel`)。
 * - `repeat_count` 是這一則被併進來幾次(後端 30 分鐘內的收斂規則)。
 *   「只改一次」與「改了十次」在畫面上要分得出來。
 */
export interface NotificationItem {
  id: number;
  kind: NotificationKind;
  title: string;
  body: string;
  link: string;
  entity_type: string;
  entity_id: number | null;
  actor_user_id: number | null;
  /** 誰造成的。畫面上那一句「王小明 改了這張單」要靠它,只有 id 寫不出名字。 */
  actor_name: string;
  repeat_count: number;
  read_at: IsoDateTime | null;
  created_at: IsoDateTime;
}

/** 未讀數。鈴鐺上那個數字就是它,前端每 30 秒問一次。 */
export interface UnreadCount {
  count: number;
}

/* ---------- 附件(第十二輪,設計規格 §3) ---------- */

/**
 * 附件的用途。`GENERAL` 是一般附件;`SIGNED_BACK` 是客戶回簽的掃描件,
 * 第 13 輪才會有動線送得出那個值,這一輪的上傳一律是 `GENERAL`。
 */
export type AttachmentKind = "GENERAL" | "SIGNED_BACK";

/**
 * 報價單上的一個附件,對應後端的 `AttachmentOut`。
 *
 * **`id` 是歸屬那一列,`file_id` 是檔案本身。** 兩個要分得開:刪附件刪的是歸屬
 * (`DELETE /api/quotes/{id}/attachments/{id}`),下載拿的是檔案。
 *
 * `download_url` 由後端給,**前端不要自己拼**。檔案一律經過認證的 API
 * (設計規格 §3.3),網址的形狀是後端的事 —— 前端自己拼的那一天,
 * 就是有人把它拼成靜態路徑的那一天。前端只負責接上掛載的根路徑
 * (`client.ts` 的 `fileUrl`)。
 *
 * `size_bytes` 是整數位元組,「1.2 MB」那一串由前端格式化 ——
 * 後端送死的字串進來,前端就再也沒辦法照欄寬決定要寫幾位小數。
 */
export interface Attachment {
  id: number;
  file_id: number;
  kind: AttachmentKind;
  note: string;
  original_name: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  uploaded_by_user_id: number | null;
  /** 誰傳的。只有 id 寫不出名字,而完整的使用者清單是老闆專屬的端點。 */
  uploaded_by_name: string;
  created_at: IsoDateTime;
  download_url: string;
}

/* ---------- 開票申請(第 13 輪,設計規格 §3) ---------- */

/**
 * 一筆開票申請的狀態。
 *
 * 這一輪只推得到 `PENDING`:真正開票、回填發票號碼在第 15 輪,現在一行
 * Amego API 都不打。另外兩個值先在型別裡,是為了不必為了兩個字串再改一次
 * 型別檔 —— 與後端的 `InvoiceRequestStatus` 一字不差。
 */
export type InvoiceRequestStatus = "PENDING" | "ISSUED" | "REJECTED";

/**
 * 一筆開票申請,對應後端的 `InvoiceRequestOut`。
 *
 * `status_label` **由後端給,前端不自己維護一份中文對照表**(設計規格 §1.4):
 * 兩份遲早會漂開,而漂開的症狀是第 15 輪把 `ISSUED` 推起來的那天,畫面上
 * 直接顯示英文。狀態一律帶文字,不可只靠顏色。
 *
 * `needs_manual_issue` 為真就是「API 開不出這個日期,要請財務手動開立並上傳」
 * (路線圖 §7.3:Amego 的 `/json/f0401` 沒有任何日期欄位)。它是後端推導的,
 * 前端**不要自己再判斷一次** —— 兩邊的判斷只要有一次對不上,畫面說的就與
 * 第 15 輪真正會走的那條路不同。
 *
 * `payment_term_id` 可能是 `null`:那一期被刪掉了(`ON DELETE SET NULL`),
 * 這筆申請與它的金額仍然算數,只是說不出當初對應的是哪一期。
 */
/** 開票申請底下開出去的一張發票(2026-10-15)。 */
export interface RequestInvoice {
  id: number;
  invoice_no: string;
  invoice_date: IsoDate | null;
  amount: Money;
  status: string;
  status_label: string;
}

export interface InvoiceRequest {
  id: number;
  quote_id: number;
  quote_no: string;
  quote_title: string;
  payment_term_id: number | null;
  payment_term_seq: number | null;
  payment_term_name: string;
  amount: Money;
  requested_by: number | null;
  requester_name: string;
  requested_at: IsoDateTime;
  /** 想要的發票日期。`null` 就是「開今天的」。 */
  wanted_date: IsoDate | null;
  note: string;
  status: InvoiceRequestStatus;
  status_label: string;
  needs_manual_issue: boolean;
  handled_by: number | null;
  handled_at: IsoDateTime | null;
  /** 買受人(報價單的客戶),登錄視窗先帶出來給財務對。 */
  buyer_name: string;
  buyer_tax_id: string;
  /** 這筆申請開出去的發票(可能不只一張);空的 = 申請中。舊的回應沒有這一欄。 */
  invoices?: RequestInvoice[];
}

/**
 * 提一筆開票申請要送的內容,對應後端的 `InvoiceRequestCreate`。
 *
 * `amount` **自己送一份,不由後端從期別讀**:追加與折讓是實務常態,而那一期的
 * 金額會跟著報價單總額走 —— 讀它的話,今天提的申請明天可能就變成另一個數字,
 * 而財務照著它開的發票已經寄出去了。
 *
 * `quote_id` 不在這裡,它在網址上:收在 body 裡就會出現「網址說 7、body 說 9」
 * 這種答不出來的請求。
 */
export interface InvoiceRequestCreate {
  payment_term_id: number | null;
  amount: Money;
  wanted_date: IsoDate | null;
  note: string;
}

/* ------------------------------------------------------------------ 發票(第 15 輪) */

/** 這張發票是怎麼開出來的。報表上兩者的發票日可信度不一樣。 */
export type InvoiceSource = "API" | "MANUAL";

/**
 * 發票狀態。`UNKNOWN` 是最重要的一個:**打了 API 但不知道結果**(逾時、
 * 連線中斷)。逾時不等於沒開成功 —— 自動重打就是開兩張,所以那一格要有人去看。
 */
export type InvoiceStatus = "PENDING" | "ISSUED" | "VOID" | "UNKNOWN";

export interface InvoicePayment {
  id: number;
  invoice_id: number;
  paid_on: IsoDate;
  amount: Money;
  note: string;
  recorded_by: number | null;
  created_at: IsoDateTime;
}

export interface Invoice {
  id: number;
  quote_id: number;
  quote_no: string;
  quote_title: string;
  payment_term_id: number | null;
  invoice_request_id: number | null;
  order_id: string;
  invoice_no: string;
  invoice_date: IsoDate | null;
  amount: Money;
  tax_amount: Money;
  buyer_tax_id: string;
  buyer_name: string;
  source: InvoiceSource;
  status: InvoiceStatus;
  /** 中文字樣由後端給 —— 前端再寫一份,兩份遲早會漂開。 */
  status_label: string;
  source_label: string;
  random_number: string;
  file_id: number | null;
  issued_by: number | null;
  created_at: IsoDateTime;
  void_reason: string;
  voided_at: IsoDateTime | null;
  paid_amount: Money;
  /** 還差多少。**多收時是負數**,不夾成 0 —— 夾掉的話畫面上看不出多收了。 */
  outstanding: Money;
  is_settled: boolean;
  /** 這張是在測試模式下開的嗎?**測試發票不算營收**。 */
  is_test: boolean;
  /** 逾期幾天。0 = 沒逾期(沒填請款日的也是 0 —— 沒填就不催)。 */
  days_overdue: number;
  payments: InvoicePayment[];
}

export interface InvoiceList {
  items: Invoice[];
  total: number;
  page: number;
  size: number;
  /** 應收總額。**只加還欠錢的那幾張** —— 多收的不該把別張欠的抵掉。 */
  total_outstanding: Money;
}

export interface InvoiceIssueManual {
  invoice_no: string;
  invoice_date: IsoDate;
  amount: Money;
  tax_amount?: Money;
  file_id?: number | null;
}

export interface PaymentInput {
  paid_on: IsoDate;
  amount: Money;
  note?: string;
}

/** 電子發票的模式。`TEST` 用 Amego 公開的測試憑證,`LIVE` 用正式那一組。 */
export type EInvoiceMode = "TEST" | "LIVE";

/**
 * 電子發票的模式與憑證狀態。
 *
 * **絕不含統編或金鑰** —— 畫面要知道的只是「現在哪一種模式」與「那顆鈕能不能按」。
 */
export interface EInvoiceStatus {
  mode: EInvoiceMode;
  configured: boolean;
}

/* ------------------------------------------------------------------ 雜支(第 16 輪) */

/**
 * 雜支的分類。**固定的十一種,不做可維護的分類表** ——
 * 第 17 輪的月 / 季 / 年報價值在於跨年度比得起來,而分類一旦可自由增刪,
 * 今年的「交通」明年被拆成「計程車」「高鐵」,兩年的數字就並排不了。
 */
/**
 * 分類代碼。原本十一種(TRANSPORT、MEAL…)照舊,**2026-09-30 起可以自己增減**,
 * 新增的由後端配一個代碼 —— 所以是一段字,不是固定的清單。
 */
export type ExpenseCategory = string;

/** 分類與它的中文字樣。**字樣由後端給** —— 前端不自己寫一份。 */
export interface ExpenseCategoryOption {
  value: ExpenseCategory;
  label: string;
  /** 幾筆雜支在用(含回收桶裡的)。 */
  in_use?: number;
  /** 刪得掉嗎:有人在用、或是「其他」都刪不掉。 */
  deletable?: boolean;
}

export interface Expense {
  id: number;
  /** `null` 就是**公司共同費用** —— 辦公室的影印紙不屬於任何專案。 */
  quote_id: number | null;
  quote_no: string;
  quote_title: string;
  category: ExpenseCategory;
  category_label: string;
  spent_on: IsoDate;
  amount: Money;
  tax_amount: Money;
  vendor: string;
  /** **可空** —— 收據本來就沒有發票號碼。 */
  invoice_no: string;
  note: string;
  /** 憑證的種類。**它決定這一筆的稅能不能扣抵**(見後端 models.ExpenseKind)。 */
  kind: ExpenseKind;
  invoice_date: IsoDate | null;
  vendor_tax_id: string;
  file_id: number | null;
  /** 憑證電子檔的檔名與網址。**沒傳就是兩個空字串** —— 可有可無是常態。 */
  file_name: string;
  file_url: string;
  created_by: number | null;
  /** 誰登錄的。**不走核准,留痕是唯一的把關。** */
  recorder_name: string;
  created_at: IsoDateTime;
}

/** 憑證的五種。字樣與可扣抵**由後端給**(`GET /api/expense-kinds`)。 */
export type ExpenseKind = "EINVOICE" | "TRIPLICATE" | "DUPLICATE" | "RECEIPT" | "NONE";

export interface ExpenseKindOption {
  value: ExpenseKind;
  label: string;
  /** 這一種的進項稅可不可以扣抵。二聯式印著稅額卻是 `false`。 */
  deductible: boolean;
}

/**
 * 下拉裡的一張報價單。**只要認得出是哪一張所需的那幾欄** ——
 * 列表端點回的東西比這多得多,但這裡只讀這三個。
 */
export interface QuotePick {
  id: number;
  quote_no: string;
  title: string;
}

export interface ExpenseList {
  items: Expense[];
  total: number;
  page: number;
  size: number;
  total_amount: Money;
}

export interface ExpenseInput {
  quote_id?: number | null;
  category: ExpenseCategory;
  spent_on: IsoDate;
  amount: Money;
  tax_amount?: Money;
  vendor?: string;
  invoice_no?: string;
  note?: string;
  file_id?: number | null;
}

/**
 * 一個專案的收支(路線圖 §3.4)。
 *
 * `margin_percent` **沒有收入時是 `null` 而不是 0** —— 畫面上要說「還沒有收入」,
 * 不是「毛利 0%」(那兩件事不一樣)。
 *
 * `cost_recorded` 為 `false` 時毛利率不可信:那幾乎一定是「還沒報帳」。
 * **畫面上一定要標出來。**
 */
export interface Profit {
  revenue: Money;
  cost: Money;
  profit: Money;
  margin_percent: Money | null;
  cost_recorded: boolean;
}


/* ---------------- 財務報表(第 17 輪) ---------------- */

/**
 * **三個數字各看各的日期**:營收看發票開立日、成本看花費日、成交看成交日。
 * 所以同一段期間的「成交」與「營收」不是同一批單 —— 畫面上要把這件事講出來。
 */
export interface ReportSummary {
  period_kind: "MONTH" | "QUARTER" | "YEAR" | "CUSTOM";
  period_label: string;
  date_from: string | null;
  date_to: string | null;
  revenue: Money;
  invoice_count: number;
  /** 歸了戶的雜支。 */
  project_cost: Money;
  /** 未歸戶的管銷。**進公司總表,不進任何專案的毛利。** */
  overhead_cost: Money;
  cost: Money;
  profit: Money;
  /** 沒有營收時是 `null` 而不是 0 —— 那兩件事不一樣。 */
  margin_percent: string | null;
  deal_amount: Money;
  deal_count: number;
  /**
   * 其中**成交日是用報價日推的**有幾張(第 13 輪之前的舊單沒有狀態事件)。
   * 不為 0 時那幾張可能落在錯的月份 —— 畫面上一定要標出來,
   * 不標的話它們看起來與正常的單一模一樣。
   */
  deals_with_estimated_date: number;
  received: Money;
  receivable: Money;
  /** 其中已逾期的。路線圖 §3.3 要它單獨一塊。 */
  overdue: Money;
  by_category: ReportCategory[];
  /** 季報三列、年報十二列;月報是空的。**空月份也有一列 0**,不是跳過。 */
  by_month: ReportMonth[];
}

export interface ReportCategory {
  category: string;
  /** 中文字樣由後端給 —— 前端不自己維護一份。 */
  label: string;
  amount: Money;
}

export interface ReportMonth {
  year: number;
  month: number;
  label: string;
  revenue: Money;
  cost: Money;
  profit: Money;
  deal_amount: Money;
  deal_count: number;
}

/** 績效表的一列。`user_id` 為 `null` 就是「未指派」那一列。 */
export interface ReportStaffRow {
  user_id: number | null;
  name: string;
  /** 離職的仍然在表上 —— 拿掉的話各業務加總會小於公司總營收。 */
  active: boolean;
  deal_amount: Money;
  deal_count: number;
  revenue: Money;
  cost: Money;
  profit: Money;
  margin_percent: string | null;
  /** 不為 0 時那一列的毛利偏高 —— 那是還沒報帳,不是真的沒有成本。 */
  quotes_without_cost: number;
}

export interface ReportStaffList {
  period_label: string;
  date_from: string | null;
  date_to: string | null;
  items: ReportStaffRow[];
}


/* ---------------- 會議紀錄(第 18 輪) ---------------- */

export type MeetingNoteKind = "MEETING" | "CLOSING" | "REVIEW";
export type MeetingLinkTargetType = "QUOTE" | "COMPANY" | "USER" | "NOTE";

/**
 * 一條 TAG 連結。
 *
 * `target_id` 為 `null` 就是**目標已經被刪掉了** —— `target_label` 仍然留著。
 * 會議紀錄是歷史文件:「那天我們討論的是 NB-2026-0007」這件事,不會因為
 * 那張單後來被刪掉就沒發生過。
 */
export interface MeetingLink {
  id?: number;
  target_type: MeetingLinkTargetType;
  target_id: number | null;
  /** 建立當下的名稱快照,不是即時 join 出來的值。 */
  target_label: string;
  target_type_label?: string;
  is_dangling?: boolean;
}

export interface MeetingNote {
  id: number;
  kind: MeetingNoteKind;
  /** 中文字樣由後端給。 */
  kind_label: string;
  quote_id: number | null;
  quote_no: string;
  quote_title: string;
  title: string;
  /** 開會那一天。**不是建立日** —— 補寫上週的紀錄是常態。 */
  met_on: string;
  attendees: string;
  body: string;
  author_name: string;
  created_by: number | null;
  links: MeetingLink[];
}

/** 列表用的一列。**沒有 `body`** —— 一百份紀錄就是一百篇文章。 */
export interface MeetingNoteRow {
  id: number;
  kind: MeetingNoteKind;
  kind_label: string;
  quote_id: number | null;
  quote_no: string;
  title: string;
  met_on: string;
  author_name: string;
  created_by: number | null;
  excerpt: string;
  link_count: number;
}

export interface MeetingNoteList {
  items: MeetingNoteRow[];
  total: number;
}


/* ---------------- 客戶端(第 14 輪) ---------------- */

export interface ClientMe {
  email: string;
  company_name: string;
  /** `true` 時整個畫面只畫得出改密碼那一張表 —— 後端也擋(409)。 */
  must_change_password: boolean;
}

export interface ClientQuote {
  id: number;
  quote_no: string;
  title: string;
  quote_date: string | null;
  total: Money;
  status: string;
  /** 中文字樣由後端給。 */
  status_label: string;
  signed_at: string | null;
  signer_name: string;
  /** 現在按得下去嗎(沒簽過、沒過期、沒被重寄作廢)。 */
  can_sign: boolean;
}

export interface ClientQuoteList {
  items: ClientQuote[];
  total: number;
}

/** 發出回簽邀請之後,回給**按下按鈕的那位同仁**的東西。 */
export interface SignoffInvite {
  link: string;
  temp_password: string;
  expires_at: string | null;
  sent_to_email: string;
}


/* ---------------- 代辦事項與信箱(第 19 輪) ---------------- */

/**
 * 一件代辦事項。
 *
 * 老闆交辦、同事互相交辦、自己的備忘錄是**同一種東西**,
 * 差別只在 `assignee_id` 指向誰。
 */
export interface Todo {
  id: number;
  title: string;
  body: string;
  /** 誰要做。 */
  assignee_id: number;
  assignee_name: string;
  /** 誰交辦的。 */
  created_by: number | null;
  author_name: string;
  due_on: string | null;
  is_done: boolean;
  /** 做完的永遠不算逾期,沒填期限的也不算。 */
  is_overdue: boolean;
  /** 自己寫給自己的備忘錄 —— 畫面上不必顯示「某某交辦」。 */
  is_self_note: boolean;
  done_at: string | null;
  created_at: string | null;
  /** 廣播待辦在**交辦端**收合起來的樣子(業主 2026-09-23:「對象追加 所有人」)。
   *
   *  只有 `scope=assigned` 而且那件事是廣播時才有值。「我要做的」那一側永遠是
   *  `null` —— 對每個人來說,廣播來的就是一件普通的待辦。 */
  broadcast: TodoBroadcast | null;
}

/** 廣播名單上的一個人。 */
export interface TodoBroadcastPerson {
  user_id: number;
  name: string;
  is_done: boolean;
}

export interface TodoBroadcast {
  done: number;
  total: number;
  /** **點開要看得出是誰還沒交** —— 只給「3/5」的話還是得自己一個個問。 */
  people: TodoBroadcastPerson[];
}

export interface TodoList {
  items: Todo[];
  total: number;
}

/* ---------------- 個人信箱(個人信箱第二階段,規格 §2.4–2.6) ---------------- */

/**
 * `GET /api/mailbox/status`。
 *
 * - `auth_failed`:郵件主機拒絕了存著的密碼(在主機上改過了),要本人重新輸入。
 * - `initial_sync_done`:第一次完整同步做完沒有。還沒做完時列表可能是空的 ——
 *   畫面要講「正在複製」,不可以讓人以為信不見了。
 * - `server_reachable`:雲端連得上嗎。連不上時照樣讀系統備份,會改雲端的按鈕停用。
 */
export interface MailboxStatus {
  enabled: boolean;
  email: string;
  last_sync_at: string | null;
  last_error: string;
  auth_failed: boolean;
  initial_sync_done: boolean;
  /** 第一次完整複製的進度(幾個資料夾做完了);做完了、或背景還沒掃過一輪是 null。 */
  initial_sync?: { folders_done: number; folders_total: number } | null;
  server_reachable: boolean;
}

/** 系統備份的用量(`GET /mailbox/backup/usage`)。 */
export interface MailBackupUsage {
  total_bytes: number;
  total_count: number;
  gone_count: number;
  gone_bytes: number;
}

export interface MailPurgePreview {
  count: number;
  bytes: number;
  samples: MailMessage[];
}

export interface MailPurgeResult {
  deleted_count: number;
  freed_bytes: number;
  skipped_count: number;
}

/** 一個資料夾。`name` 是主機上的名字(`INBOX.Sent`),`label` 是後端給的中文名。 */
export interface MailFolder {
  name: string;
  label: string;
  total: number;
  unread: number;
}

/**
 * 列表上的一封信(讀的是**系統備份**)。
 *
 * `id` 是備份索引的主鍵,不是 IMAP UID —— 雲端已刪除的信沒有 UID 可用。
 * `deleted_on_server`:雲端已經沒有這封了,備份還在;讀得到、下載得到,但不能改旗標或刪除。
 */
export interface MailMessage {
  id: number;
  folder: string;
  from_addr: string;
  from_name: string;
  to_addrs: string[];
  subject: string;
  date: string | null;
  seen: boolean;
  flagged: boolean;
  has_attachments: boolean;
  deleted_on_server: boolean;
}

export interface MailAttachment {
  part: number;
  filename: string;
  size: number;
  content_type: string;
}

/**
 * 讀一封信(`GET /api/mailbox/messages/{id}`)。
 *
 * `html` 已在後端清洗過(script、事件屬性、表單都拿掉了);遠端圖片的 `src` 預設**整個拿掉**,
 * 數量記在 `remote_images_blocked`,帶 `?images=1` 重抓才會留著。`cid:` 內嵌圖片已經換成
 * `data:` URI;換不了的記在 `inline_images_dropped`(「顯示圖片」救不回它們)。
 * 純文字信的 `html` 是空字串。
 */
export interface MailDetail extends MailMessage {
  text: string;
  html: string;
  attachments: MailAttachment[];
  remote_images_blocked: number;
  inline_images_dropped: number;
  /** 讀信時的警告(§2.6):冒名、危險附件、外部。舊的假資料可能沒有,當成空陣列。 */
  warnings?: MailWarning[];
  /** 對到的客戶與最近的報價單(§2.6)。 */
  clients?: MailClientMatch[];
  /** 信裡第一個行事曆邀請(最多一個);沒有是空陣列。 */
  calendar?: MailCalendarEvent[];
  /** 本人設了、還沒完成的待回覆提醒的到期時間;沒有是 null。 */
  followup_due?: string | null;
}

/** `impersonation` / `dangerous_attachment` 是紅色警告;`external` 是灰色小標。 */
export interface MailWarning {
  kind: "impersonation" | "dangerous_attachment" | "external" | string;
  text: string;
}

export interface MailClientQuote {
  id: number;
  quote_no: string;
  title: string;
  status: QuoteStatus;
  updated_at: string | null;
}

export interface MailClientMatch {
  company_id: number;
  company_name: string;
  quotes: MailClientQuote[];
}

/** 行事曆邀請。時間是 ISO 字串:全天是 `2026-12-12`,有時區的帶時差,認不得時區的不帶。 */
export interface MailCalendarEvent {
  summary: string;
  start: string;
  end: string;
  location: string;
  organizer: string;
}

/** 對話串列表的一列:最新一封的摘要 + 這一串幾封、幾封未讀、參與者。 */
export interface MailThreadRow extends MailMessage {
  thread_key: string;
  count: number;
  unread: number;
  participants: string[];
}

export interface MailThreadPage {
  items: MailThreadRow[];
  next_before: number | null;
}

/** `POST /mailbox/batch`。`partial: true`(200)= 中途連線斷了,`failed` 的沒做成。 */
export interface MailBatchResult {
  done: number[];
  skipped: number[];
  unchanged: number[];
  failed: number[];
  partial: boolean;
  sync_pending: boolean;
}

/** 進階搜尋條件(與後端 `search.SearchParams` 同名;`from` 是寄件人)。沒設的欄位 = 不篩。 */
export interface MailSearchParams {
  q?: string;
  folder?: string;
  date_from?: string;
  date_to?: string;
  has_attachments?: boolean;
  attachment?: string;
  from?: string;
  to?: string;
  flagged?: boolean;
  unread?: boolean;
}

export interface MailSavedSearch {
  id: number;
  name: string;
  params: MailSearchParams;
  created_at: string | null;
}

/** 系統保管、還沒寄出的排程信。 */
export interface MailScheduled {
  id: number;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  send_at: string;
  status: string;
  attachments: { filename: string; size: number }[];
  created_at: string | null;
}

/** 列表的一頁。`next_before` 是下一頁的游標(最後一封的 id);沒有下一頁是 `null`。 */
export interface MailPage {
  items: MailMessage[];
  next_before: number | null;
}

/** `POST /api/mailbox/send` 的回應。`refused` 是郵件主機不收的收件人(其他人照樣寄到了)。 */
export interface MailSendResult {
  sent: boolean;
  message_id?: string;
  sync_pending?: boolean;
  refused: string[];
}

/* ---------------- 匯出與列印(第 19 輪) ---------------- */

export type ExportFormat = "pdf" | "xlsx";

/**
 * 要哪一份。
 *
 * - `all` 整張報價單(預設,與這一輪之前一模一樣)
 * - `hide` 只有明細 —— **整份文件沒有錢**,不是把價格欄留白
 */
export type ExportScope = "all" | "hide";

/** PDF 的版本(業主 2026-09-25):standard = 一頁式;full = 每章一頁。 */
export type PdfLayout = "standard" | "full";


/* ---------------- 上下班打卡(第 20 輪) ---------------- */

/**
 * 一次打卡。
 *
 * **沒有「遲到」這種欄位** —— 業主選的是「只記時間,不判遲到」。
 * 這一行的班表本來就不規則,訂一個標準只會讓畫面滿是沒有意義的紅字。
 */
export interface Punch {
  id: number;
  kind: "IN" | "OUT";
  punched_at: string;
  /** 事後補登的。業主要的「留痕」就是它。 */
  is_manual: boolean;
  note: string;
}

/** 某人某一天的出勤。時鐘那一塊與老闆總表的每一列都是這個形狀。 */
export interface AttendanceDay {
  user_id: number;
  name: string;
  work_date: string;
  first_in: string | null;
  /** 還在上班時是 null —— **不可以拿「現在」去補**。 */
  last_out: string | null;
  /** 工時,一位小數的字串。時數與錢一樣不走浮點數。 */
  hours: string;
  working: boolean;
  /** 「未打卡 / 上班中 / 已下班」。狀態不可只靠顏色,所以後端就給字。 */
  state_label: string;
  manual_count: number;
  punch_count: number;
  punches: Punch[];
}

export interface AttendanceBoardData {
  day: string;
  rows: AttendanceDay[];
}

/** 那一天請的是什麼假。沒請就是 `null`。 */
export interface LeaveOnDay {
  leave_id: number;
  kind: string;
  /** 中文字樣(「特休」)。**後端給** —— 前端自己寫一份,兩份會漂開。 */
  label: string;
  /** `FULL` / `AM` / `PM`。**半天假那一天照樣有打卡。** */
  period: string;
  /** `APPROVED` / `PENDING`。還沒准的假不該看起來像已經准了。 */
  status: string;
  /** 按小時的假那一張的時數;整天/半天是 null。 */
  hours?: number | null;
  /** 按小時的假從幾點開始;沒記的是 null。 */
  start_hour?: number | null;
}

/** 出勤月表的一列:那一天的出勤,外加「那天請了什麼假」。 */
export interface AttendanceMonthDay extends AttendanceDay {
  /** 星期幾,1 是星期一(後端的 `isoweekday`)。 */
  weekday: number;
  leave: LeaveOnDay | null;
}

/** 一整個月。**每一天都有一列**,含週末與沒打卡的日子。 */
export interface AttendanceMonth {
  user_id: number;
  name: string;
  year: number;
  month: number;
  days: AttendanceMonthDay[];
  worked_days: number;
  /** 當月總工時,一位小數的字串。 */
  total_hours: string;
  /** 請了幾天假,半天是 0.5(字串,例如 "2.5")。 */
  leave_days: string | number;
}

/* ---------- 對帳:銷項(應收)與進項(應付) ---------- */
//
// 業主 2026-09-19:「財務 缺了兩個總表 …… 不然是要怎麼對帳」
//
// 發票頁回答「我今天要處理什麼」,這兩張回答「這一期總共幾張、未稅多少、
// 稅額多少」—— **那三個數字就是申報書上要填的格子。**

/** 合計。**申報書上要填的就是這幾格。** */
export interface ReconcileTotals {
  count: number;
  net: string;
  tax: string;
  gross: string;
  /** 銷項才有。 */
  received: string;
  outstanding: string;
  /** 進項才有:**真正抵得掉的稅**。不能扣抵的那幾筆不算在裡面。 */
  deductible_tax: string;
}

export interface SalesRow {
  invoice_id: number;
  invoice_no: string;
  invoice_date: string | null;
  buyer_tax_id: string;
  buyer_name: string;
  net: string;
  tax: string;
  gross: string;
  status: string;
  /** 作廢的是 false。**列得出來但不算進合計** —— 不列的話號碼會跳號。 */
  counted: boolean;
  received: string;
  outstanding: string;
  quote_id: number;
  quote_no: string;
  quote_title: string;
}

export interface PurchaseRow {
  expense_id: number;
  invoice_no: string;
  invoice_date: string | null;
  vendor_tax_id: string;
  vendor: string;
  net: string;
  tax: string;
  gross: string;
  kind: string;
  kind_label: string;
  /** 這筆的稅抵不抵得掉。**與「稅額是 0」是兩件事。** */
  deductible: boolean;
  /** 掛在哪一張單,或他寫的事項。**每一筆帳目都要清清楚楚**(業主原話)。 */
  belongs_to: string;
  quote_id: number | null;
  quote_no: string;
  source: string;
}

export interface SalesSheet {
  period_label: string;
  date_from: string;
  date_to: string;
  rows: SalesRow[];
  totals: ReconcileTotals;
}

export interface PurchaseSheet {
  period_label: string;
  date_from: string;
  date_to: string;
  rows: PurchaseRow[];
  totals: ReconcileTotals;
}

/* ---------- 內文裡的 @ 提及(第 22 輪) ---------- */
//
// 業主 2026-09-19:「連結功能我覺得做得很差 根本只是HTAG /
//                我要的是直接可以在內文提及 客戶打 @之後 跳出要TAG的類別/項目/人員」
//
// **不做多層下拉**:人記得的是名字(「介壽堂」),不是它屬於哪一類。
// 一次回一張混合清單,類別只當每一列右邊的標籤。

export type MentionType = "QUOTE" | "COMPANY" | "USER" | "NOTE" | "ITEM";

export interface MentionHit {
  type: MentionType;
  /** 中文字樣由後端給(全站規矩)。 */
  type_label: string;
  id: number;
  label: string;
}

export interface MentionList {
  items: MentionHit[];
}
